/**
 * Where a login link lands, owned by one module instead of passed between three.
 *
 * ## What went wrong, so that the shape of this file makes sense
 *
 * The destination used to travel from the screen that spends the magic-link token to the
 * component that performs the redirect **through the address bar**. Three actors, one shared
 * mutable medium, no contract:
 *
 *   1. a bootstrap script in `<head>` takes the token out of the URL (`login-token-url.ts`);
 *   2. the login screen POSTs the token, then rewrites the URL to drop the spent credential;
 *   3. `AuthGuard`, on a later render, reads `redirect_url` out of the URL and pushes it.
 *
 * Step 2 wrote back a copy of the query taken at the **first** render, and on a cold load Next
 * leaves `router.query` empty until hydration finishes — `useRouter()` hands each render a
 * frozen copy of it, which the in-flight login callback then held for the length of the
 * request. So the copy written back was `{}`, the destination was erased before step 3 looked
 * for it, and the worker landed on `/profilo`. Measured in production: `/profilo` traffic
 * roughly doubled on 3 September 2026 and stayed there for twenty days.
 *
 * ## The three rules this file exists to hold
 *
 * **One reader.** `resolveLoginDestination` is the only way to ask where to go. `AuthGuard`
 * used to read `router.query['redirect_url']` itself, unvalidated, which meant the sanitising
 * the login screen did guarded the pipe and not the tap.
 *
 * **One source of truth, in precedence order.** The login response first, because since
 * September 2026 the backend records the destination on the credential's own row and answers
 * with it — a value we minted, which cannot have been edited in transit and therefore cannot
 * point off-site. Then the destination remembered from the address bar **as the page was
 * opened**, which is what makes this immune to anything that rewrites the URL later. Then the
 * live address bar, for a visitor who arrived at `/login` without a token at all.
 *
 * **Nothing is pushed that has not been through `internalPath`.** Both on write and on read.
 */

import { trackLoginDestination, trackLoginDestinationLost } from './track-login-destination';

/**
 * The destination, reduced to a path inside this app, or nothing.
 *
 * Whatever survives here is somewhere the browser will go: the guard hands it to
 * `router.push`. So the rule is not "looks internal", it is "the parser agrees it is
 * internal".
 *
 * **Asking whether the string starts with `/` and not with `//` is not enough.** The browser
 * resolves a URL by the WHATWG rules, and those turn a backslash into a slash and drop tabs
 * and newlines outright, so `/\evil.example` and a tab between the two slashes both arrive at
 * `https://evil.example/` while passing that test. Anything reasoning about a URL by reading
 * its first two characters is guessing at a parser, so the parser answers instead: the value
 * is resolved against an origin that cannot exist, and it is a path only if it lands back on
 * that same origin. What comes out is the resolved path, not the string handed in, so the
 * value that reaches `router.push` is the one already agreed on.
 *
 * Dropping a destination costs the worker a redirect. Keeping the wrong one costs them the
 * session.
 */
const NOWHERE = 'https://internal.invalid';

/** `/login`, `/signup` and their locale-prefixed forms: nowhere a post-login redirect may go. */
const LOGIN_ROUTE = /^(?:\/[a-z]{2})?\/(?:login|signup)\/?$/;

export const internalPath = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.startsWith('/')) return undefined;
  try {
    const resolved = new URL(value, NOWHERE);
    if (resolved.origin !== NOWHERE) return undefined;
    const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    if (path.startsWith('//')) return undefined;

    // A destination that points back at the login route is refused: not an open redirect, but
    // a loop, and the plain `/login` case ends on `/profilo` — the bug this file exists for.
    return LOGIN_ROUTE.test(resolved.pathname) ? undefined : path;
  } catch {
    return undefined;
  }
};

/** The destination as the address bar has it **right now**. */
export const destinationFromLocation = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    return internalPath(new URLSearchParams(window.location.search).get('redirect_url'));
  } catch {
    return undefined;
  }
};

/**
 * What the address bar said when this page was opened.
 *
 * Module state, and deliberately not React state: the value has to outlive every render, every
 * `router.replace`, and the in-flight login request that sits between them. It is the same
 * shape `consumeLoginTokenFromUrl` uses for the token, and for the same reason — the two
 * halves of a magic link should not have different lifetimes, which is exactly the coupling
 * whose removal caused the September regression.
 */
let arrival: string | undefined;
let arrivalRead = false;

/** Outcomes already reported in this tab. See `resolveAndReportLoginDestination`. */
const reported = new Set<string>();

/**
 * Capture the destination before anything can rewrite the URL.
 *
 * Call it as early as the app can run code — module scope of `_app`, next to
 * `consumeLoginTokenFromUrl` — and then as often as you like: it reads once.
 */
export function rememberArrivalDestination(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (arrivalRead) return arrival;

  arrivalRead = true;
  arrival = destinationFromLocation();
  return arrival;
}

/** The remembered destination, without reading the URL again. */
export function arrivalDestination(): string | undefined {
  return arrival;
}

/**
 * Forget it.
 *
 * Nothing in the app calls this today, and that is deliberate rather than an oversight: a
 * second magic link is opened as a new document, which resets this module anyway, and clearing
 * it mid-flow would take the destination away from `AuthGuard`, which reads it *after* the
 * login screen has finished. It exists because module state that cannot be reset is module
 * state that leaks between tests, and because the day somebody does need to clear it they
 * should not have to reach into the variable.
 */
export function clearArrivalDestination(): void {
  arrival = undefined;
  arrivalRead = false;
  reported.clear();
}

/** Test seam: the module state has to be resettable between cases. */
export const resetLoginDestinationForTests = clearArrivalDestination;

export interface ResolvedLoginDestination {
  path?: string;
  source: 'response' | 'address_bar' | 'none';
}

/**
 * Where to send someone who has just signed in, and where the answer came from.
 *
 * `fromResponse` is `redirect_url` off the `POST /accounts/login` body. It wins because it is
 * the only copy nobody outside our backend could have written; it is still put through
 * `internalPath`, because "we minted it" is an argument about the normal path and this is the
 * line that would be load-bearing on the abnormal one.
 */
export function resolveLoginDestination(fromResponse?: unknown): ResolvedLoginDestination {
  const fromBackend = internalPath(fromResponse);
  if (fromBackend) return { path: fromBackend, source: 'response' };

  // The remembered value before the live one: by the time this is called the login screen has
  // usually rewritten the URL, and the whole point of remembering is that such a rewrite cannot
  // take the destination with it.
  const fromUrl = arrivalDestination() ?? destinationFromLocation();
  if (fromUrl) return { path: fromUrl, source: 'address_bar' };

  return { source: 'none' };
}

/**
 * Resolve, report, and say whether the two copies of the destination disagree.
 *
 * The reporting is not decoration. The September defect produced no error, no failed request
 * and no moved counter: it was found because `/profilo` traffic had doubled in an unrelated
 * dashboard.
 *
 * ## Why there is no "the destination was lost" alarm here, and why that is correct
 *
 * This function used to carry one, phrased as the property rather than a branch — *if the link
 * arrived with a destination and we are going somewhere else, something dropped it.* It could
 * never fire, and the reason is worth keeping because it is the same reason the bug is fixed:
 * `resolveLoginDestination` **falls back to `arrivalDestination()`**, so whenever an arrival
 * exists it is also what we resolve to. Comparing where we land against the arrival is
 * comparing a value with itself. Any client-side "was it lost" check is tautological by
 * construction, because the resolver and the alarm read the same two sources.
 *
 * Put the other way round: the September failure — the URL rewritten mid-login and the
 * destination gone with it — is no longer expressible in the browser, because the arrival is
 * captured at module load and survives every later rewrite. An alarm for it would be an alarm
 * for something that cannot happen.
 *
 * What *is* detectable here is the one thing the two sources can do independently: **disagree**.
 * If the backend answers a destination different from the one the link arrived with, something
 * rewrote one of them, and that is worth a line.
 *
 * The question this cannot answer — *did a link that carried a destination arrive without one?*
 * — is answerable only where both the token and the URL are visible, which is the backend:
 * `event=login_attempt destination=token|url|none`. That is the regression signal to watch, and
 * it is server-side on purpose.
 */
export function resolveAndReportLoginDestination(
  fromResponse: unknown,
  surface: 'login_screen' | 'auth_guard',
  fallback?: string
): ResolvedLoginDestination {
  const resolved = resolveLoginDestination(fromResponse);
  const arrived = arrivalDestination();

  // `AuthGuard` calls this during render, and a render happens more than once before the
  // navigation it decides on completes — twice more under React 19's StrictMode. One line per
  // outcome per tab, so the O2 counts are of logins and not of renders.
  const seen = `${surface}:${resolved.source}:${resolved.path ?? ''}`;
  if (reported.has(seen)) return resolved;
  reported.add(seen);

  trackLoginDestination({
    source: resolved.source,
    arrivedWithDestination: !!arrived,
    destination: resolved.path,
    surface,
  });

  const landingOn = resolved.path ?? fallback;

  // Reachable in exactly one shape, and the name says which: `arrived` being truthy forces
  // `resolved.path` to be truthy too (the fallback above), so `landingOn` is the resolved path,
  // and it can only differ from the arrival when the backend answered something else.
  if (arrived && landingOn !== arrived) {
    trackLoginDestinationLost({
      arrived,
      landedOn: landingOn ?? 'unknown',
      reason: 'response_differs_from_arrival',
      surface,
    });
  }

  return resolved;
}
